import { F } from '@/utils/intl';
import { Button, Checkbox, Flex, Input, Modal, Popover, Select } from 'antd';
import React, { useState } from 'react';
import { useIntl, useModel } from 'umi';
import { cancelServiceRequestRequest } from '@/services/api/serviceRequest';
import { CloseCircleOutlined } from '@ant-design/icons';
import { CheckAccess } from '@/utils/access';
import PERMISSIONS from '@/constants/permissions';
import { useHasAccess } from '@/utils/hooks';
import { processSingleServiceRequest } from '@/components/Kanban/ServiceRequests/Calendar/utils';

const OTHER_VALUE = '__other__';

const CancelServiceRequestAction = ({
  children = null,
  serviceRequest,
  setLoading = (loading: boolean) => {},
  onSuccess = () => {},
  buttonSize = 'small',
  showActionText = false,
  open: externalOpen,
  onOpenChange = (open: boolean) => {},
  getPopupContainer,
  // When the action lives inside a "…" dropdown menu, its trigger button is
  // rendered hidden (display:none), and a Popover can't anchor to a box-less
  // element — so render the reason capture as a modal instead.
  modal = false,
  ...buttonProps
}) => {
  const intl = useIntl();
  const { initialState } = useModel('@@initialState');
  const possibleReasons: string[] = initialState?.currentUser?.Partner?.possible_reasons_for_cancellation || [];
  const isReasonRequired: boolean = initialState?.currentUser?.Partner?.is_cancellation_reason_required ?? false;

  // Service-request cancellation notifications are gated by control_emails
  // (consistent with reschedule): holders may choose, everyone else always notifies.
  const hasControlEmails = useHasAccess([PERMISSIONS.ServiceRequests.CONTROL_EMAILS, PERMISSIONS.BUSINESS_PARTNER]);
  const showEmailChoice = hasControlEmails;

  const [internalOpen, setInternalOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string | undefined>(undefined);
  const [otherReason, setOtherReason] = useState('');
  const [sendEmail, setSendEmail] = useState(true);

  const isOpen = externalOpen ?? internalOpen;

  const handleOpenChange = (open: boolean) => {
    setInternalOpen(open);
    onOpenChange(open);
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedReason(undefined);
    setOtherReason('');
    setSendEmail(true);
    handleOpenChange(true);
  };

  const handleClose = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    handleOpenChange(false);
  };

  const handleConfirm = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const reason = possibleReasons.length === 0
      ? otherReason.trim()
      : selectedReason === OTHER_VALUE ? otherReason.trim() : selectedReason;

    setConfirmLoading(true);
    if (setLoading) setLoading(true);

    try {
      const data: Record<string, any> = {};
      if (reason) data.cancellation_reason = reason;
      // Only holders of control_emails decide; others fall back to always-notify.
      if (showEmailChoice) data.send_email = sendEmail;
      await cancelServiceRequestRequest({
        params: { serviceRequestId: serviceRequest.id },
        data,
      }).then(onSuccess);
      handleOpenChange(false);
    } finally {
      setConfirmLoading(false);
      if (setLoading) setLoading(false);
    }
  };

  const isConfirmDisabled = (() => {
    if (!isReasonRequired) return false;
    if (possibleReasons.length > 0) {
      if (!selectedReason) return true;
      return selectedReason === OTHER_VALUE && !otherReason.trim();
    }
    return !otherReason.trim();
  })();

  if (!processSingleServiceRequest(serviceRequest).isCancellable) return null;

  const selectOptions = [
    ...possibleReasons.map((r) => ({ label: r, value: r })),
    {
      label: intl.formatMessage({ id: 'common.other', defaultMessage: 'Other' }),
      value: OTHER_VALUE,
    },
  ];

  const reasonLabel = (
    <label>
      {isReasonRequired && <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>}
      <F id="pages.serviceRequests.cancellationReason" defaultMessage="Cancellation Reason" />
    </label>
  );

  const popoverContent = (
    <Flex vertical gap={12} style={{ width: modal ? '100%' : 280 }} onClick={(e) => e.stopPropagation()}>
      {reasonLabel}
      {possibleReasons.length > 0 ? (
        <>
          <Select
            placeholder={intl.formatMessage({
              id: 'pages.serviceRequests.cancelAppointmentModal.selectReason',
              defaultMessage: 'Select a reason',
            })}
            value={selectedReason}
            onChange={(v) => {
              setSelectedReason(v);
              setOtherReason('');
            }}
            style={{ width: '100%' }}
            options={selectOptions}
            // Keep the options dropdown in the same container as the popover so
            // a surface that closes on outside-click (the chip) doesn't tear the
            // panel down when a reason is picked. Defaults to body elsewhere.
            getPopupContainer={getPopupContainer}
          />
          {selectedReason === OTHER_VALUE && (
            <Input.TextArea
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
              placeholder={intl.formatMessage({
                id: 'pages.serviceRequests.cancelAppointmentModal.otherReason',
                defaultMessage: 'Please specify a reason',
              })}
              rows={3}
              autoFocus
            />
          )}
        </>
      ) : (
        <Input.TextArea
          value={otherReason}
          onChange={(e) => setOtherReason(e.target.value)}
          placeholder={intl.formatMessage(
            isReasonRequired
              ? { id: 'pages.serviceRequests.cancelAppointmentModal.otherReason', defaultMessage: 'Please specify a reason' }
              : { id: 'pages.serviceRequests.cancelAppointmentModal.optionalReason', defaultMessage: 'Reason (optional)' },
          )}
          rows={3}
          autoFocus
        />
      )}
      {showEmailChoice && (
        <Checkbox
          checked={sendEmail}
          data-testid="service-request-cancel-send-email"
          onChange={(e) => setSendEmail(e.target.checked)}
        >
          <F id="pages.serviceRequest.cancelSendEmail" defaultMessage="Notify the customer by email" />
        </Checkbox>
      )}
      <Flex justify="flex-end" gap={8}>
        <Button size="small" onClick={handleClose} data-testid="service-request-cancel-close-btn">
          <F id="common.actions.close" defaultMessage="Close" />
        </Button>
        <Button
          size="small"
          color="danger"
          variant="filled"
          disabled={isConfirmDisabled}
          loading={confirmLoading}
          onClick={handleConfirm}
          data-testid="service-request-cancel-confirm-btn"
        >
          <F id="common.actions.cancel_appointment" defaultMessage="Cancel" />
        </Button>
      </Flex>
    </Flex>
  );

  if (modal) {
    return (
      <CheckAccess access={[PERMISSIONS.ServiceRequests.CANCEL, PERMISSIONS.BUSINESS_PARTNER]}>
        <Modal
          open={isOpen}
          onCancel={() => handleClose()}
          footer={null}
          destroyOnClose
          // Compact modal so its width tracks the reason input (which now fills
          // it) rather than the default 520px with the input floating in it.
          width={340}
          title={<F id="pages.serviceRequests.cancelAppointmentModal.title" defaultMessage="Cancel Appointment" />}
        >
          {popoverContent}
        </Modal>
      </CheckAccess>
    );
  }

  return (
    <CheckAccess access={[PERMISSIONS.ServiceRequests.CANCEL, PERMISSIONS.BUSINESS_PARTNER]}>
      <Popover
        open={isOpen}
        onOpenChange={(v) => { if (!v) handleClose(); }}
        trigger="click"
        getPopupContainer={getPopupContainer}
        title={<F id="pages.serviceRequests.cancelAppointmentModal.title" defaultMessage="Cancel Appointment" />}
        content={popoverContent}
      >
        <Button
          size={buttonSize as any}
          shape="round"
          color="danger"
          variant="filled"
          data-testid="service-request-cancel-btn"
          icon={<CloseCircleOutlined />}
          onClick={handleButtonClick}
          {...buttonProps}
        >
          {showActionText && (
            <F id="common.actions.cancel_appointment" defaultMessage="Cancel" />
          )}
        </Button>
      </Popover>
    </CheckAccess>
  );
};

export default CancelServiceRequestAction;
